import React from 'react'
import { Image, Pressable, Text, View } from 'react-native'
import { twMerge } from 'tailwind-merge'
import { tv } from 'tailwind-variants'

import { body2 } from '@ui-kit/core/typography'
import { theme } from '@ui-kit/theme'

import type { RadioSetComponentProps as Props } from './types'

const radioButton = tv({
  slots: {
    container: 'mb-4 flex flex-row items-center active:opacity-70',
    radioCircle:
      'mr-1.5 flex h-6 w-6 flex-row items-center justify-center rounded-full border border-grey-500 bg-white',
    radioInnerCircle: 'h-3 w-3 rounded-full',
    labelIcon: 'mr-2.5 h-2.5 w-6 object-contain',
  },
  variants: {
    isRow: {
      true: {
        container: 'mb-2.5 mr-5 flex-grow-0',
      },
    },
    isLast: {
      true: {},
    },
    checked: {
      true: {
        radioCircle: 'border-black',
        radioInnerCircle: 'bg-black',
      },
    },
    hasError: {
      true: {
        radioCircle: 'border-error',
        radioInnerCircle: 'bg-error',
      },
    },
  },
  compoundVariants: [
    {
      isLast: true,
      isRow: false,
      className: 'mb-0',
    },
    {
      isLast: true,
      isRow: true,
      className: 'mr-0',
    },
  ],
  defaultVariants: {
    isRow: false,
  },
})

const RadioButton = (props: Props) => {
  const onPress = async () => {
    if (props.disabled) {
      return
    }

    await props.onPress(props.value)
  }

  const { container, radioCircle, radioInnerCircle, labelIcon } = radioButton({
    isRow: props.isRow,
    isLast: props.isLast,
    checked: props.checked,
    hasError: props.hasError,
  })

  return (
    <Pressable
      className={container()}
      onPress={onPress}
      hitSlop={{ top: theme.space.xsmall, bottom: theme.space.xsmall }}
      disabled={props.disabled}
      testID={props.testID}
    >
      <View className={radioCircle()}>
        <View className={radioInnerCircle()} />
      </View>
      {props.image && <Image className={labelIcon()} resizeMode="contain" source={props.image} />}
      <Text
        className={body2({
          className: twMerge('flex-shrink leading-8', props.disabled && 'text-grey-500'),
        })}
      >
        {props.label}
      </Text>
    </Pressable>
  )
}

export default RadioButton
